<template>
    <div>
        <v-form v-if="ready" ref="form" @submit.prevent v-model="isValid">
            <fe-label class="mt-0">Visualization Name</fe-label>
            <v-text-field
                v-model="config.name"
                counter=50
                :rules="this.$fieldValidators('text', null, {required: true, limit: 50})"
                flat solo dense clearable autofocus
            />

            <fe-remote-exclude-combo
                label='Incident Type (Optional)'
                :url='incidentBehaviorTypeUrl'
                rootProperty='incident_behavior_type'
                v-model="savedSearch.incident_behavior_type_id"
                class="remote-select"
            />

            <fe-remote-exclude-combo
                label='Incident Code (Optional)'
                :items="incidentBehaviors"
                itemText="display_name"
                v-model="savedSearch.incident_behavior_id"
                :disabled="this.$_.isEmpty(savedSearch.incident_behavior_type_id)"
            />

            <fe-label>Include Secondary Incidents & Responses</fe-label>
            <fe-switch v-model="config.details"/>

            <fe-remote-combo
                label="Report As"
                :items="reportAsOptions"
                v-model="config.report_as"
                :rules="this.$fieldValidators('select', null, {required: true})"
                byId
            />

            <group-by v-model="config.chart_types" :visType="config.config.type"/>

            <vis-type v-model="config.visualization"/>

            <student-filters
                v-if="!globalFilters"
                :filters="studentFilters"
                :dashboard="dashboard"
                @change="studentFilters = $event"
            />

            <fe-label>Enter a Short Description</fe-label>
            <v-textarea
                v-model="config.description"
                counter=255
                rows=4
                :rules="this.$fieldValidators('text', null, {required: false, limit: 255})"
                flat solo dense no-resize
            />

            <display-settings :value="displaySettings" @change="displaySettings = $event"/>
        </v-form>
    </div>
</template>

<script>
import VisType from "./fields/VisType"
import GroupBy from "./fields/GroupBy"
import StudentFilters from "./fields/StudentFilters"
import DisplaySettings from "./fields/DisplaySettings"

export default {
    name: "IncidentType",

    components: {
        VisType,
        GroupBy,
        StudentFilters,
        DisplaySettings
    },

    props: {
        dashboard: {
            type: Object,
            required: true
        },
        edit: {
            type: Object,
            required: false
        },
        getSSValues: {
            type: Function,
            required: true
        },
        getSchoolYears: {
            type: Function,
            required: true
        },
        setConfig: {
            type: Function,
            required: true
        },
        globalFilters: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            ready: false,
            isValid: false,
            config: {
                name: '',
                chart_types: undefined,
                visualization: undefined,
                details: undefined,
                report_as: undefined,
                colors: [],
                yAxisMin: undefined,
                yAxisMax: undefined,
                description: '',
                config: {
                    type: 'incidentType',
                    chart_types: undefined,
                    visualization: undefined,
                    details: undefined,
                    report_as: undefined,
                    colors: [],
                    yAxisMin: undefined,
                    yAxisMax: undefined,
                    description: ''
                }
            },
            savedSearch: {
                incident_behavior_type_id: undefined,
                incident_behavior_id: undefined,
                school_id: undefined,
                grade_id: undefined,
                ethnicity: undefined,
                disability_type_id: undefined,
                gender: undefined,
                tag_id: undefined,
                meal_status_id: undefined,
                ell_flag: undefined,
                ell_level_id: undefined,
                student_active_flag: undefined,
                student_active_date: undefined,
                demographic: undefined
            },
            incidentBehaviors: []
        }
    },

    computed: {
        displaySettings: {
            get() {
                return {
                    colors: this.config.colors,
                    yAxisMin: this.config.yAxisMin,
                    yAxisMax: this.config.yAxisMax
                }
            },

            set(v) {
                Object.keys(v).forEach(x => {
                    if (this.config.hasOwnProperty(x)) {
                        this.config[x] = v[x]
                    }
                })
            }
        },

        reportAsOptions() {
            let opts = [{
                id: 'incident_total',
                name: 'Number of Incidents'
            }]

            if (this.config.details) {
                opts.push({
                    id: 'code_total',
                    name: 'Number of Reported Codes'
                })
            }

            opts.push({
                id: 'incident_average',
                name: 'Average Incidents Per Day'
            }, {
                id: 'student_total',
                name: 'Total Students'
            }, {
                id: 'student_percent',
                name: 'Percent of Students'
            })

            return opts
        },

        studentFilters: {
            get() {
                return {
                    school_id: this.savedSearch.school_id,
                    grade_id: this.savedSearch.grade_id,
                    ethnicity: this.savedSearch.ethnicity,
                    disability_type_id: this.savedSearch.disability_type_id,
                    gender: this.savedSearch.gender,
                    tag_id: this.savedSearch.tag_id,
                    meal_status_id: this.savedSearch.meal_status_id,
                    ell_flag: this.savedSearch.ell_flag,
                    ell_level_id: this.savedSearch.ell_level_id,
                    student_active_flag: this.savedSearch.student_active_flag,
                    student_active_date: this.savedSearch.student_active_date,
                    demographic: this.savedSearch.demographic
                }
            },

            set(v) {
                Object.keys(v).forEach(x => {
                    if (this.savedSearch.hasOwnProperty(x)) {
                        this.savedSearch[x] = v[x]
                    }
                })
            }
        },

        incidentBehaviorTypeUrl() {
            return this.$models.getUrl('incidentBehaviorType', 'read') + '&active=1'
        }
    },

    watch: {
        isValid(n, o) {
            if (n !== o) {
                this.$emit('valid', n)
            }
        },

        config: {
            deep: true,
            handler(v) {
                this.$emit('change', v, 'config')
            }
        },

        savedSearch: {
            deep: true,
            handler(v) {
                this.$emit('change', v, 'savedSearch')
            }
        },

        'savedSearch.incident_behavior_type_id': {
            immediate: true,
            deep: true,
            handler(n, o) {
                if (o && !this.$_.isEqual(n, o)) {
                    this.savedSearch.incident_behavior_id = []
                }
                if (!this.$_.isEmpty(n)) {
                    this.setIncidentBehaviors()
                }
            }
        },
    },

    mounted() {
        let awaiting = []

        if (this.edit) {
            this.config = this.setConfig(this.config, this.edit)
            awaiting.push(this.getSSValues(this.config.saved_search_id, this.savedSearch))
        }

        Promise.all(awaiting)
            .then(() => {
                this.setColors()
                if (this.globalFilters) {
                    this.getSSValues(this.dashboard.saved_search_id, this.savedSearch)
                        .then(() => {
                            this.ready = true
                        })
                } else {
                    this.ready = true
                }
            })
    },

    methods: {
        setColors() {
            let years = this.getSchoolYears(this.dashboard.school_year_name).reverse()
            let colors = this.config.colors
            this.config.colors = years.map(x => {
                let color = colors[x.name] || null
                return {
                    name: x.name,
                    id: x.name,
                    color: color
                }
            })
        },

        setIncidentBehaviors() {
            let incidentBehaviorTypes = this.savedSearch.incident_behavior_type_id
            if (!this.$_.isEmpty(incidentBehaviorTypes)) {
                let typeIds = incidentBehaviorTypes.map(x => x.rcExclude ? '!' + x.id : x.id)
                let url = this.$models.getUrl('incidentBehavior', 'read') + '&incident_behavior_type_id=' + typeIds.join(',')
                this.$axios.get(url)
                    .then(res => {
                        this.incidentBehaviors = this.$_.sortBy(res.data.incident_behavior, o => o.display_name)
                    })
            } else {
                this.incidentBehaviors = []
            }
        }
    }
}
</script>

<style lang="scss" scoped>

</style>
